import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "../../config/axiosClient";
import Config from "../../config/Config";
import { RESOLVE_LOCAL_AUTH_STATE, RESOLVE_LOCAL_USER_STATE } from "../auth/AuthActions";

const API_BASE = Config().baseUrl;

const formatTimestamp = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMin / 60);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.floor(diffHr / 24);
  if (diffDays === 1) return "yesterday";
  if (diffDays <= 30) return `${diffDays}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export default function CommentSection({ projectId }) {
  const [comments, setComments] = useState([]);
  const [body, setBody] = useState("");
  const [status, setStatus] = useState(null); // 'sending' | 'error'
  const [editingId, setEditingId] = useState(null);
  const [editBody, setEditBody] = useState("");
  const [loading, setLoading] = useState(true);

  const auth = RESOLVE_LOCAL_AUTH_STATE();
  const user = RESOLVE_LOCAL_USER_STATE();
  const isAuthenticated = auth.is_authenticated === "true";
  const currentUserPk = user.pk ? parseInt(user.pk, 10) : null;
  const isAdmin = currentUserPk === 1;

  const fetchComments = useCallback(() => {
    axios
      .get(`${API_BASE}${projectId}/comments/`)
      .then((res) => {
        setComments(res.data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [projectId]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setStatus("sending");
    try {
      const res = await axios.post(`${API_BASE}${projectId}/comments/`, { body });
      setComments((prev) => [res.data, ...prev]);
      setBody("");
      setStatus(null);
    } catch {
      setStatus("error");
    }
  };

  const handleEdit = async (commentId) => {
    if (!editBody.trim()) return;
    try {
      const res = await axios.patch(`${API_BASE}${projectId}/comments/${commentId}/`, {
        body: editBody,
      });
      setComments((prev) =>
        prev.map((c) => (c.id === commentId ? res.data : c))
      );
      setEditingId(null);
      setEditBody("");
    } catch {
      // keep editing state on failure
    }
  };

  const handleDelete = async (commentId) => {
    try {
      await axios.delete(`${API_BASE}${projectId}/comments/${commentId}/`);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch {
      // silent
    }
  };

  return (
    <div style={{ marginTop: 40, borderTop: "1px solid var(--border, #eee)", paddingTop: 24 }}>
      <h5 style={{ fontWeight: 700, marginBottom: 16, color: "var(--ink)" }}>
        Comments{comments.length > 0 && ` (${comments.length})`}
      </h5>

      {/* Comment input */}
      {isAuthenticated ? (
        <form onSubmit={handleSubmit} style={{ marginBottom: 24 }}>
          <textarea
            placeholder="Leave a comment..."
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={5000}
            rows={3}
            style={{
              width: "100%",
              padding: "10px 14px",
              border: "1px solid var(--border, #ddd)",
              borderRadius: 6,
              background: "var(--surface, #fff)",
              color: "var(--ink)",
              fontSize: 14,
              fontFamily: "inherit",
              resize: "vertical",
              marginBottom: 8,
              boxSizing: "border-box",
            }}
          />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 12, color: "var(--muted, #888)" }}>
              {body.length}/5000
            </span>
            <button
              type="submit"
              disabled={status === "sending" || !body.trim()}
              style={{
                padding: "8px 24px",
                border: "none",
                borderRadius: 6,
                background: "var(--brand, #59c6c1)",
                color: "#fff",
                fontSize: 14,
                fontWeight: 600,
                cursor: status === "sending" || !body.trim() ? "not-allowed" : "pointer",
                opacity: status === "sending" || !body.trim() ? 0.6 : 1,
              }}
            >
              {status === "sending" ? "Posting..." : "Post"}
            </button>
          </div>
          {status === "error" && (
            <p style={{ color: "var(--danger, #ef4444)", fontSize: 13, marginTop: 6 }}>
              Failed to post comment. Try again.
            </p>
          )}
        </form>
      ) : (
        <p style={{ fontSize: 14, color: "var(--muted, #888)", marginBottom: 24 }}>
          <Link to="/signin" style={{ color: "var(--brand, #59c6c1)", fontWeight: 600 }}>
            Sign in
          </Link>{" "}
          to leave a comment.
        </p>
      )}

      {/* Comment list */}
      {loading ? (
        <p style={{ color: "var(--muted, #888)", fontSize: 14 }}>Loading comments...</p>
      ) : comments.length === 0 ? (
        <p style={{ color: "var(--muted, #888)", fontSize: 14 }}>No comments yet.</p>
      ) : (
        <div>
          {comments.map((comment) => {
            const isOwner = currentUserPk === comment.user;
            const canDelete = isOwner || isAdmin;
            const isEditing = editingId === comment.id;
            const wasEdited = comment.updated_at !== comment.created_at;

            return (
              <div
                key={comment.id}
                style={{
                  padding: "12px 0",
                  borderBottom: "1px solid var(--border, #eee)",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Link to={`/${comment.username}`}>
                      {comment.picture_link ? (
                        <img
                          src={comment.picture_link}
                          alt=""
                          style={{
                            width: 28, height: 28, borderRadius: "50%",
                            objectFit: "cover", flexShrink: 0,
                          }}
                        />
                      ) : (
                        <div style={{
                          width: 28, height: 28, borderRadius: "50%",
                          background: "var(--brand, #59c6c1)", color: "#fff",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 13, fontWeight: 700, flexShrink: 0,
                        }}>
                          {(comment.username || "?")[0].toUpperCase()}
                        </div>
                      )}
                    </Link>
                    <div>
                      <Link
                        to={`/${comment.username}`}
                        style={{ fontWeight: 600, fontSize: 14, color: "var(--ink)", textDecoration: "none" }}
                      >
                        {comment.username}
                      </Link>
                      <span style={{ fontSize: 12, color: "var(--muted, #888)", marginLeft: 8 }}>
                        {formatTimestamp(comment.created_at)}
                        {wasEdited && " (edited)"}
                      </span>
                    </div>
                  </div>
                  {isAuthenticated && (isOwner || canDelete) && !isEditing && (
                    <div style={{ display: "flex", gap: 8 }}>
                      {isOwner && (
                        <button
                          onClick={() => { setEditingId(comment.id); setEditBody(comment.body); }}
                          style={actionBtnStyle}
                        >
                          edit
                        </button>
                      )}
                      {canDelete && (
                        <button onClick={() => handleDelete(comment.id)} style={actionBtnStyle}>
                          delete
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {isEditing ? (
                  <div>
                    <textarea
                      value={editBody}
                      onChange={(e) => setEditBody(e.target.value)}
                      maxLength={5000}
                      rows={3}
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        border: "1px solid var(--border, #ddd)",
                        borderRadius: 6,
                        background: "var(--surface, #fff)",
                        color: "var(--ink)",
                        fontSize: 14,
                        fontFamily: "inherit",
                        resize: "vertical",
                        marginBottom: 6,
                        boxSizing: "border-box",
                      }}
                    />
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        onClick={() => handleEdit(comment.id)}
                        disabled={!editBody.trim()}
                        style={{
                          padding: "4px 14px",
                          border: "none",
                          borderRadius: 4,
                          background: "var(--brand, #59c6c1)",
                          color: "#fff",
                          fontSize: 13,
                          fontWeight: 600,
                          cursor: "pointer",
                        }}
                      >
                        Save
                      </button>
                      <button
                        onClick={() => { setEditingId(null); setEditBody(""); }}
                        style={actionBtnStyle}
                      >
                        cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p style={{
                    fontSize: 14,
                    color: "var(--ink)",
                    lineHeight: 1.6,
                    margin: 0,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}>
                    {comment.body}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const actionBtnStyle = {
  background: "none",
  border: "none",
  color: "var(--muted, #888)",
  fontSize: 12,
  cursor: "pointer",
  padding: "2px 4px",
  fontFamily: "inherit",
};
